import { useCallback, useMemo, useState } from "react";
import { Button, DropdownSelect, Loading, Typography } from "@/components/atoms";
import { DataTable, EmptyState } from "@/components/molecules";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { useGetClientProfileQuery, useUpdateClientProfileMutation } from "@/redux/apis/clientProfile/clientProfileApi";
import { useLazyGetReceiptPdfQuery, useListPastReceiptsQuery } from "@/redux/apis/clientReceipts/clientReceiptsApi";
import { PastReceipt } from "@/redux/apis/clientReceipts/types";
import { addToast, base64ToBlob, downloadFile } from "@/utils";
import { generateReceiptsTableData, RECEIPT_CYCLE_OPTIONS, receiptsColumns } from "./helpers";

export const ReceiptsCard = ({ accountId }: { accountId?: string }) => {
  // Shared card: operator-managed view uses 3rd-person copy, client self-service keeps 2nd-person.
  const isOperatorView = !!accountId;
  const {
    data: profile,
    isLoading: isProfileLoading,
    error: profileError,
    refetch: refetchProfile,
  } = useGetClientProfileQuery(accountId ? { accountId } : undefined);
  const [updateClientProfile, { isLoading: isSavingCycle }] = useUpdateClientProfileMutation();

  const {
    data: receipts = [],
    isLoading: isReceiptsLoading,
    error: receiptsError,
    refetch: refetchReceipts,
  } = useListPastReceiptsQuery(accountId ? { accountId } : undefined);
  const [fetchReceiptPdf] = useLazyGetReceiptPdfQuery();

  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const cycleDay = profile?.receipts.consolidated_invoice_date;
  const isLoading = isProfileLoading || isReceiptsLoading;
  const error = profileError ?? receiptsError;

  const handleRetry = () => {
    if (profileError) refetchProfile();
    if (receiptsError) refetchReceipts();
  };

  const handleCycleChange = (value: string) => {
    const day = Number(value);
    if (!day || day === cycleDay) return;
    updateClientProfile({ accountId, receipts: { consolidated_invoice_date: day } })
      .unwrap()
      .then(() => addToast("success", "Recurring receipt date updated"))
      .catch((e) => getErrorMessages(e).forEach((message) => addToast("danger", message)));
  };

  const handleDownload = useCallback(
    (receipt: PastReceipt) => {
      setDownloadingId(receipt.id);
      const request = fetchReceiptPdf({ id: receipt.id, accountId });
      request
        .unwrap()
        .then((res) => {
          try {
            // the receipt detail returns the PDF base64-encoded inside the JSON entity
            downloadFile(base64ToBlob(res.invoice), res.filename || receipt.filename);
          } catch {
            addToast("danger", "Failed to download file.");
          }
        })
        .catch((e) => getErrorMessages(e).forEach((message) => addToast("danger", message)))
        .finally(() => {
          setDownloadingId(null);
          // release the lazy-query subscription so keepUnusedDataFor(0) can evict the base64 payload
          request.unsubscribe();
        });
    },
    [fetchReceiptPdf, accountId]
  );

  const tableData = useMemo(
    () => generateReceiptsTableData(receipts, handleDownload, downloadingId),
    [receipts, handleDownload, downloadingId]
  );

  const renderContent = () => {
    if (isLoading) return <Loading className="relative min-h-[120px] !bg-transparent" />;

    if (error)
      return (
        <div className="flex flex-col items-start gap-2">
          <Typography variant="paragraph" className="text-danger">
            {getErrorMessages(error)[0] ?? "Unable to load your receipts."}
          </Typography>
          <Button variant="secondary" size="sm" onClick={handleRetry}>
            Try again
          </Button>
        </div>
      );

    return (
      <>
        <div className="flex flex-col gap-1">
          <Typography variant="action" className="text-neutral-dark-gray">
            Set recurring date cycle
          </Typography>
          {/* key remounts the Listbox when the profile value arrives/changes — DropdownSelect
              seeds headlessui with defaultValue, so the selected highlight is set only on mount */}
          <DropdownSelect
            key={cycleDay ?? "unset"}
            options={RECEIPT_CYCLE_OPTIONS}
            value={cycleDay ? String(cycleDay) : undefined}
            placeholder="Select a date"
            size="md"
            disabled={isSavingCycle}
            onChange={handleCycleChange}
          />
          <Typography variant="paragraph" className="text-neutral-silver-gray">
            {isOperatorView
              ? "Consolidated receipts can be mailed to the client on a set date for the previous 30 days. E.g. select the 7th to consolidate receipts for the 7th-6th"
              : "Consolidated receipts can be mailed to you on a set date for the previous 30 days. E.g. select the 7th to consolidate receipts for the 7th-6th"}
          </Typography>
        </div>

        <div className="flex flex-col gap-4">
          <Typography variant="title" className="text-neutral-silver-gray">
            Past monthly receipts (12 months)
          </Typography>
          <div className="relative overflow-hidden rounded-lg border border-neutral-mid-gray">
            {downloadingId && <Loading className="rounded-lg" />}
            <DataTable
              className="w-full overflow-x-auto !rounded-none !ring-0 [&_table]:table-auto [&_tr]:bg-white"
              data={tableData}
              columns={receiptsColumns}
              emptyState={<EmptyState title="No Receipts" description="There are currently no receipts available" />}
            />
            {receipts.length > 0 && (
              <div className="flex items-center border-t border-neutral-mid-gray bg-neutral-surface-gray px-3 py-2">
                <Typography variant="small" className="whitespace-nowrap text-neutral-silver-gray">
                  1–{receipts.length} of {receipts.length}
                </Typography>
              </div>
            )}
          </div>
        </div>
      </>
    );
  };

  return (
    <div className="flex flex-col gap-5 rounded-lg border border-neutral-mid-gray bg-white p-5">
      <div className="flex flex-col gap-1">
        <Typography variant="title" className="text-neutral-silver-gray">
          Receipts
        </Typography>
        <Typography variant="paragraph" className="text-neutral-dark-gray">
          {isOperatorView
            ? "This is a consolidated report of all receipts in relation to the client's credit card charges only."
            : "This is a consolidated report of all receipts in relation to your credit card charges only."}
        </Typography>
      </div>

      {renderContent()}
    </div>
  );
};
